import { Warning } from '@mui/icons-material';
import { Alert, Box, Dialog, DialogContent, Typography } from '@mui/material';
import { getCreditLineByProduct } from 'lib/api/creditLine';
import { submitSGRWarrantyRequest } from 'lib/api/express/sgrWarrantyRequest';
import { deleteSGRWarrantyRequest } from 'lib/api/sgr';
import { SGR_TYPES } from 'lib/consts';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { errorHandler } from 'lib/functions/errors';
import { Warranty } from 'lib/types/models';
import React from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

import WarrantyDetailTabs from './WarrantyDetailTabs';

interface WarrantyDetailDialogProps {
  open: boolean;
  onClose: () => void;
  warranty: Warranty | null;
  refreshWarranties: () => Promise<void>;
}

const WarrantyDetailDialog: React.FC<WarrantyDetailDialogProps> = ({
  open,
  onClose,
  warranty,
  refreshWarranties,
}) => {
  const dispatch = useDispatch();
  const domain = window.location.hostname;
  const secretId = domain.split('.')[0];
  const [loadingDelete, setLoadingDelete] = useState(false);
  const auth = useSelector((state: RootState) => state.auth);
  const businessIdentifier = auth.user?.businessIdentifier ?? null;
  const { data: creditLineData, isPending } = getCreditLineByProduct(
    dispatch,
    businessIdentifier,
    'sgr',
  );
  const { mutate, isPending: isSubmitting } =
    submitSGRWarrantyRequest(dispatch);

  const canRequestWarranty = useMemo(() => {
    if (!warranty || !creditLineData) return false;
    const subProduct = creditLineData.subProducts?.find(
      (sp) => sp.subProductId === warranty.type,
    );
    return subProduct && creditLineData.status === 'ACTIVE';
  }, [creditLineData, warranty]);

  if (!warranty) return null;
  const warrantyType = SGR_TYPES.find(
    (type) => type.value === warranty.type,
  )?.label;

  const handleDeleteWarranty = async () => {
    try {
      setLoadingDelete(true);
      if (!warranty?.id) return;
      await deleteSGRWarrantyRequest(dispatch, warranty?.id);
      onClose();
      void refreshWarranties();
    } catch (error: unknown) {
      errorHandler(dispatch, error);
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleRequestWarranty = async () => {
    mutate(
      { id: warranty?.id ?? 0, secretId },
      {
        onSuccess: () => {
          onClose();
          void refreshWarranties();
        },
      },
    );
  };

  if (isPending) return <CenteredCircularProgress />;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        {!canRequestWarranty && warranty.status === 'QUOTED' && (
          <Alert
            severity="warning"
            icon={<Warning />}
            sx={{
              borderRadius: 2,
              mb: 3,
              '& .MuiAlert-message': {
                fontSize: '0.9rem',
              },
            }}
          >
            <Typography variant="subtitle2" fontWeight="medium" mb={1}>
              Línea de Crédito Requerida
            </Typography>
            <Typography variant="body2">
              No tienes línea de crédito activa para el tipo de garantía &quot;
              {warrantyType}&quot;. Contacta a tu ejecutivo para solicitar una
              línea de crédito.
            </Typography>
          </Alert>
        )}

        <WarrantyDetailTabs warranty={warranty} />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            flexDirection: 'row',
            gap: 2,
            mt: 3,
          }}
        >
          {warranty.status === 'QUOTED' && (
            <>
              <CustomButton
                color="error"
                onClick={handleDeleteWarranty}
                loading={loadingDelete}
              >
                Eliminar Cotización
              </CustomButton>
              <CustomButton
                color="primary"
                onClick={handleRequestWarranty}
                disabled={!canRequestWarranty}
                loading={isSubmitting}
              >
                Solicitar Garantía
              </CustomButton>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WarrantyDetailDialog;
